import React, { 
  useCallback, useMemo
} from 'react';
import { useHistory } from 'react-router-dom';

import isFunction from 'lodash/isFunction';
import { parseISO } from 'date-fns';

import formatRelativeDate from '../../../utils/format_relative_date';
import { truncateText } from '../../../utils/text_utils';

import ContentTags from '../../ContentTags/ContentTags';
import DataTable from '../../common/DataTable/DataTable';
import Label from '../../common/Labels/Label';
import LinkLabel from '../../common/Labels/LinkLabel';
import PipelineStatusWithPopover from '../PipelineStatusBadge/PipelineStatusWithPopover';
import { preserveWorkspaceURL } from '../../../utils/url_utils';

const transformPipelinesToTableRows = (pipelineGroups = []) => {
  const pipelineKeys = Object.keys(pipelineGroups);
  return pipelineKeys?.map((pipelineID) => {
    const pipeline = pipelineGroups[pipelineID][0];
    const versions = pipelineGroups[pipelineID];
    return ({
      pipelineID,
      latestVersion: pipeline.latestVersion,
      pipelineTags: pipeline.pipelineTags,
      status: versions.some(element => element.status === 'Active') ? 'Active' : 'Inactive',
      createdAt: pipeline.createdAt,
      updatedAt: pipeline.updatedAt,
      lastVersionUpdate: pipeline.latestVersion?.createdAt,
    })
  });
}

const RelativeDateCellRenderer = ({ value }) => formatRelativeDate(parseISO(value));
const StatusCellRenderer = ({ row, value }) => <PipelineStatusWithPopover deploymentUpdatedAt={row?.original?.latestVersion?.deployments?.[0]?.updatedAt} deployed={row?.original?.latestVersion?.deployments?.[0]?.deployed} status={value} />
const LatestVersionCellRenderer = ({ row, value }) => {
  const pipelineID = row?.original?.pipelineID
  const latestVersion = row?.original?.latestVersion?.version;

  const truncatedVersion = useMemo(() => truncateText(latestVersion), [latestVersion]);

  return (
    <div className="flex flex-row flex-wrap items-center p-2">
      {
        latestVersion !== undefined ? (
          <>
            <LinkLabel
              to={preserveWorkspaceURL(`/pipelines/${pipelineID}/versions/${latestVersion}`)}
              title={latestVersion}
              bold
            >
              {truncatedVersion}
            </LinkLabel>
            <span className="mx-1">&middot;</span>
          </>
        ) : null
      }
      {
        value ? (
          <Label>{formatRelativeDate(parseISO(value))}</Label>
        ) : (
          <Label>N/A</Label>
        )
      }
    </div>
  )
}
const PipelineCellRenderer = ({ row, value }) => {
  const latestVersion = row?.original?.latestVersion?.version;
  const pipelineTags = row?.original?.pipelineTags?.map(({ tag }) => tag.tag);

  const truncatedName = truncateText(value, 30);

  return (
    <div className="flex flex-col p-2">
      <div className="flex flex-row flex-wrap items-center mb-2">
        <LinkLabel
          to={preserveWorkspaceURL(`/pipelines/${value}/versions/${latestVersion}`)}
          size="2xl"
          title={value}
          bold
        >
          {truncatedName}
        </LinkLabel>
      </div>
      <ContentTags tags={pipelineTags} />
    </div>
  )
}

export const PipelineDataTable = ({
  groupedPipelines = []
}) => {
  const history = useHistory();

  const columns = useMemo(() => [
    {
      Header: 'Pipeline',
      accessor: 'pipelineID',
      Cell: PipelineCellRenderer,
      width: 240
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: StatusCellRenderer,
      width: 80
    },
    {
      Header: 'Created At',
      accessor: 'createdAt',
      Cell: RelativeDateCellRenderer,
      width: 80,
    },
    {
      Header: 'Latest Version',
      accessor: 'lastVersionUpdate',
      Cell: LatestVersionCellRenderer,
      width: 120,
    },
  ], []);

  const data = useMemo(() => transformPipelinesToTableRows(groupedPipelines), [groupedPipelines]);
  const defaultColumn = React.useMemo(
    () => ({
      width: 160,
    }),
    []
  )

  const pushHistory = history?.push;
  const onItemClick = useCallback((row) => {
    const pipelineID = row?.original?.pipelineID
    const pipelineVersion = row?.original?.latestVersion?.version;
    if (pipelineID && pipelineVersion && isFunction(pushHistory)) {
      pushHistory(preserveWorkspaceURL(`/pipelines/${pipelineID}/versions/${pipelineVersion}`)(history.location))
    }
  }, [history.location, pushHistory]);

  return (
    <DataTable
      id="pipeline-data-table"
      columns={columns}
      data={data}
      defaultColumn={defaultColumn}
      onItemClick={onItemClick}
    />
  )
};

export default PipelineDataTable;
